// User types
export interface User {
  id: number;
  telegram_id: number;
  username?: string;
  first_name?: string;
  last_name?: string;
  language_code: string;
  is_premium: boolean;
  is_admin?: boolean;
  balance: number;
  referral_code: string;
  referred_by_id?: number;
  referral_earnings: number;
  email?: string;
  auth_type?: 'telegram' | 'web';
  created_at: string;
  last_active: string;
}

export interface AuthResponse {
  success: boolean;
  user: User;
}

// Raffle types
export enum RaffleStatus {
  PENDING = 'pending',
  ACTIVE = 'active',
  DRAWING = 'drawing',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export enum RaffleCategory {
  ELECTRONICS = 'electronics',
  TRANSPORT = 'transport',
  GAMING = 'gaming',
  MONEY = 'money',
  ACCESSORIES = 'accessories',
  OTHER = 'other',
}

export interface PrizeItem {
  place: number;
  name: string;
  photo_url?: string;
}

export interface WinnerItem {
  place: number;
  user_id: number;
  prize_name: string;
  username?: string;
  first_name?: string;
}

export interface Raffle {
  id: number;
  title: string;
  description?: string;
  photo_url?: string;
  prizes?: PrizeItem[];
  entry_price: number;
  max_participants?: number;
  status: RaffleStatus;
  category: RaffleCategory;
  participants_count: number;
  winners?: WinnerItem[];
  start_time?: string;
  draw_time?: string;
  end_time?: string;
  created_at: string;
}

export interface ParticipantInfo {
  user_id: number;
  username?: string;
  first_name?: string;
  tickets_count: number;
  amount_paid: number;
}

export interface RaffleDetail extends Raffle {
  participants: ParticipantInfo[];
  draw_data?: any;
}

export interface RaffleEntryRequest {
  raffle_id: number;
  user_ticket_id: number;
}

// Transaction types
export enum TransactionType {
  DEPOSIT = 'deposit',
  WITHDRAW = 'withdraw',
  RAFFLE_ENTRY = 'raffle_entry',
  RAFFLE_WIN = 'raffle_win',
  REFERRAL_BONUS = 'referral_bonus',
  PLATFORM_FEE = 'platform_fee',
  JACKPOT_BET = 'jackpot_bet',
  JACKPOT_WIN = 'jackpot_win',
  REFUND = 'refund',
}

export enum TransactionStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

export interface Transaction {
  id: number;
  user_id: number;
  type: TransactionType;
  status: TransactionStatus;
  amount: number;
  balance_before: number;
  balance_after: number;
  raffle_id?: number;
  payment_id?: string;
  payment_method?: string;
  description?: string;
  metadata?: {
    raffle_title?: string;
    tickets_count?: number;
    referrer_username?: string;
    [key: string]: unknown;
  };
  created_at: string;
  updated_at?: string;
}

// Referral types
export interface ReferralStats {
  referral_code: string;
  total_referrals: number;
  total_earnings: number;
  active_referrals: number;
}

export interface ReferralUserInfo {
  user_id: number;
  username?: string;
  first_name?: string;
  total_earned: number;
  created_at: string;
}

// API Request/Response types
export interface DepositRequest {
  amount: number;
  payment_method?: string;
}

export interface ApiError {
  detail: string;
}

// Jackpot types
export enum JackpotStatus {
  ACTIVE = 'active',
  ROLLING = 'rolling',
  COMPLETED = 'completed',
}

export interface JackpotBet {
  id: number;
  jackpot_id: number;
  user_id: number;
  username?: string;
  first_name?: string;
  amount: number;
  chance: number;
  created_at: string;
}

export interface Jackpot {
  id: number;
  status: JackpotStatus;
  total_pool: number;
  winner_id?: number;
  winner_prize?: number;
  platform_commission?: number;
  timer_end?: string;
  created_at: string;
  completed_at?: string;
  draw_data?: any;
  bets: JackpotBet[];
}

export interface JackpotBetRequest {
  amount: number;
}

// Ticket types
export interface TicketImage {
  id: number;
  filename: string;
  name: string;
  price: number;
  is_active: boolean;
  created_at: string;
}

export interface UserTicket {
  id: number;
  user_id: number;
  ticket_image_id: number;
  price_paid: number;
  is_used: boolean;
  raffle_id?: number;
  used_at?: string;
  created_at: string;
  image_filename: string;
  image_name: string;
}

export interface TicketBuyRequest {
  ticket_image_id: number;
}

export interface RaffleEntryWithTicketRequest {
  raffle_id: number;
  user_ticket_id: number;
}

// Gamification types
export interface AchievementInfo {
  key: string;
  name: string;
  description: string;
  icon: string;
  xp_reward: number;
  unlocked: boolean;
  unlocked_at?: string | null;
}

export interface GamificationProfile {
  xp: number;
  level: number;
  level_name: string;
  next_level_xp: number;
  xp_progress_pct: number;
  achievements: AchievementInfo[];
}

export interface LeaderboardEntry {
  rank: number;
  user_id: number;
  first_name?: string;
  username?: string;
  xp: number;
  level: number;
  level_name: string;
}

export interface AdminStats {
  users: {
    total: number;
    new_today: number;
    new_week: number;
  };
  revenue: {
    total: number;
    month: number;
  };
  raffles: {
    active: number;
    pending: number;
    completed: number;
    cancelled: number;
    total: number;
  };
  tickets: {
    total_sold: number;
    total_pool: number;
  };
}
